// The Productivity Chart panel's view state: which window is being looked at, at what
// granularity, and which Production Group is overlaid on it - plus the three query parameters that
// carry those three answers in the URL.

import { computed, watch, type Ref } from 'vue';
import { useRouteQuery } from '@/composables/useRouteQuery';
import type { ProductivityGranularity, ProductivityRange } from '@/plugins/store/actions/queries/active-groups';
import { RANGE_OPTIONS, resolveRangeOption, type ProductivityRangeOption } from './productivityRanges';

// Range, granularity, the comparison reference and which cameras are switched off are what one person
// means by "look at this" when sending another the link, so they travel as query parameters. The six
// metric toggles do not: a personal reading preference rather than shared context.
//
// The hidden cameras are on the carried side of that line because "it is camera 2 dragging this down,
// look at the rest without it" is a finding about the Section, not a preference about how to read it.
// Whether the readout row's camera breakdown is *expanded* is the preference, and stays in the
// component.
const QUERY_RANGE = 'range';
const QUERY_GRANULARITY = 'granularity';
const QUERY_COMPARE = 'compare';
const QUERY_HIDE_CAMERAS = 'hideCameras';

// Hourly or daily (default) buckets. The same two the sibling Weight modal offers, but held here
// rather than through useChartGranularity - see the granularity ref below for why.
const GRANULARITY_OPTIONS: ProductivityGranularity[] = ['hour', 'day'];
const DEFAULT_GRANULARITY: ProductivityGranularity = 'day';

export function useProductivityViewState(input: {
    sectionId: Ref<number | null>;
    // The running Production Group's start (ISO yyyy-MM-dd), or null when the Section has none right now.
    productionGroupStart: Ref<string | null>;
}) {
    // All three parameters go through useRouteQuery, the app's own route-state idiom (the same one
    // useNotificationsSearchParams and useRecordsSearchParams use). It already provides every
    // property this needs: a default fallback, drop-when-default, `replace`
    // rather than `push`, touching only its own key, batching several keys into one navigation on
    // nextTick, and a `transform` hook - which is exactly where validating a value against the
    // options that exist belongs.
    //
    // The query is read synchronously here rather than in `onMounted`: applied a tick later, the
    // range holds its default for that tick, so opening a link carrying `range=6m` fires a fetch for
    // the default 3 months and then immediately a second for the 6 months actually asked for.

    // No default is passed, so this is never dropped from the URL. Deliberate: its default is not a
    // constant but "Production Group" or "3 months" depending on whether the open Section has one
    // running - data that arrives after mount. Dropped-when-default, that asynchrony silently strips
    // `range` from a link opened before the Production Group resolves, leaving the chart showing the
    // right range and the URL no longer saying so.
    const defaultRangeOption = (): ProductivityRangeOption => (input.productionGroupStart.value !== null ? 'productionGroup' : '3m');
    const rangeOption = useRouteQuery<string | undefined, ProductivityRangeOption>(QUERY_RANGE, undefined, {
        transform: (raw) => ((RANGE_OPTIONS as string[]).includes(raw as string) ? (raw as ProductivityRangeOption) : defaultRangeOption()),
    });

    // Dropped when it is the default, since that default IS a constant: omitting it says the same
    // thing as writing it, and a link carrying what was chosen reads better than one restating what
    // would have happened anyway.
    //
    // A plain URL-bound ref rather than useChartGranularity: that composable exists to reset the
    // granularity when the open Section changes, and this surface wants the opposite - stepping
    // through a barn at hour grain and being dropped back to day on each step is the wrong answer.
    // Asking for a composable and switching off the only thing it does is asking for a ref.
    const granularity = useRouteQuery<string, ProductivityGranularity>(QUERY_GRANULARITY, DEFAULT_GRANULARITY, {
        transform: (raw) => ((GRANULARITY_OPTIONS as string[]).includes(raw as string) ? (raw as ProductivityGranularity) : DEFAULT_GRANULARITY),
    });

    // The comparison reference as the reader picked it: an id, which the caller resolves against
    // the Section's Production Group history. Holding the id rather than the candidate is what lets a
    // pick survive a range change that takes the control away - the pick is still what the reader
    // asked for, and comes back when the range does. What it does NOT survive is a change of
    // Section; see the watch below.
    const selectedComparisonId = useRouteQuery<string | null, number | null>(QUERY_COMPARE, null, {
        transform: (raw) => (raw !== null && raw !== undefined && Number.isFinite(Number(raw)) ? Number(raw) : null),
    });

    // The cameras the reader has switched off, as Sensor ids - see resolveHiddenCameras for why the
    // device and not its position in the roster.
    //
    // One comma-separated parameter through a plain string ref rather than an array bound to the query:
    // useRouteQuery drops a parameter by comparing to the default with `===`, which no two arrays satisfy,
    // so switching the last camera back on would leave `hideCameras=` in the URL saying nothing.
    //
    // Validated no further than "positive whole numbers": whether an id names a camera the open Section
    // has is a question about a camera list this composable does not hold.
    const hiddenCamerasParam = useRouteQuery<string | null, string | null>(QUERY_HIDE_CAMERAS, null, {
        transform: (raw) => (typeof raw === 'string' ? raw : null),
    });

    const hiddenCameraSensorIds = computed<number[]>({
        get: () =>
            (hiddenCamerasParam.value ?? '')
                .split(',')
                .map((part) => Number(part.trim()))
                .filter((sensorId) => Number.isInteger(sensorId) && sensorId >= 1),
        // Sorted on the way out so the same set of hidden cameras produces the same link whichever
        // order the reader clicked them in, and null - not an empty string - when the last one comes
        // back on, which is what takes the parameter out of the URL rather than emptying it.
        set: (next) => {
            hiddenCamerasParam.value = next.length === 0 ? null : [...next].sort((left, right) => left - right).join(',');
        },
    });

    // A picked reference is one of *this* Section's own Production Groups, so stepping through the barn
    // has to drop it. Holding the id was meant to make the pick self-invalidating - a candidate list
    // it is no longer on draws nothing - but the list is derived from the barn's whole history, so a
    // period belonging to the Section two doors down still resolves, still gets fetched, and still
    // stretches the axis to make room for a halo the picker is simultaneously reporting as
    // unavailable. Cleared on a real change only: the same parameter arriving in an opened link is
    // the reader's own choice about the Section they opened, and survives.
    // The hidden cameras go with it. They do self-invalidate, since a Sensor id belongs to one Section,
    // but clearing them keeps the URL from carrying a hide that applies to nothing on screen.
    watch(input.sectionId, (next, previous) => {
        if (previous === null || next === previous) return;
        selectedComparisonId.value = null;
        hiddenCameraSensorIds.value = [];
    });

    // `new Date()` is read, not tracked: the range re-resolves when the reader changes something,
    // and deliberately not when the clock passes midnight. A range that moved on its own would
    // refetch under a reader who asked for nothing, and the axis already states the present it was
    // drawn against (`directory_local_now`), which is the figure a stale window would contradict.
    const range = computed<ProductivityRange | null>(() => {
        if (input.sectionId.value === null) return null;
        return resolveRangeOption(rangeOption.value, input.productionGroupStart.value, new Date());
    });

    // Every granularity is offered over every range. Deliberately: the reader asking to see a long
    // batch hour by hour is asking for something real, and whether a given combination is *readable*
    // is a judgement the reader is better placed to make than either side of the wire. A server-side
    // bucket cap mirrored into a disabled button here would answer it by taking the control away.
    //
    // The server keeps one absolute ceiling, far above anything these controls can produce (twelve
    // months hourly is roughly nine thousand buckets), purely so a hand-written URL cannot ask for a
    // century. If a real range ever reaches it, the panel's error state offers a retry and a coarser
    // grain rather than a dead end.
    const granularityOptions = GRANULARITY_OPTIONS;

    return {
        rangeOption,
        range,
        granularity,
        granularityOptions,
        selectedComparisonId,
        hiddenCameraSensorIds,
    };
}
